// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { ChangeStatusJobModalComponent } from '~/components/module/job';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import useDebounce from '~/utils/hooks/useDebounce';
import { IJob, IQueryGetListJob } from '~/utils/interface/job';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamById } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  jobFilterPaymentOptions,
  POUND,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import {
  JobFilterPaymentEnum,
  PageTypeEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
} from '~/utils/enum/common';
import { formatTitleCase } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deleteJob, getJobListByCustomer } from '~/thunks/job/jobThunk';
import { StorageEnum } from '~/utils/enum/base';
import { jobActions, selectIsRefreshJobByCustomerList } from '~/thunks/job/jobSlice';
// Styles, images, icons
import styles from './CustomerJobs.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onNavigateJob: (jobId: string) => void,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onChangeStatus: (job: IJob) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IJob>[] => {
  return [
    {
      id: 'jobId',
      label: t('table_head_title_id'),
      render: (record: IJob) => {
        return record?.id ? (
          <Box component={'div'} className={cx('jobIdContainer')}>
            <Typography
              className={cx('jobId')}
              variant='body1'
              onClick={(e) => {
                e.stopPropagation();
                onNavigateJob(record?.id);
              }}
            >
              {POUND}
              {record?.id}
            </Typography>

            {record?.isMissingPayment && (
              <Tooltip
                title={t('table_tooltip_job_missing_payment')}
                arrow
                placement='top'
                slots={{ transition: Zoom }}
              >
                <img
                  src={icons.commonIconWarning}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('iconWarning')}
                />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box component={'div'}> </Box>
        );
      },
    },
    {
      id: 'name',
      label: t('table_head_title_name'),
      render: (record: IJob) => {
        return record?.name || EMPTY_STRING;
      },
    },
    {
      id: 'address',
      label: t('table_head_title_address'),
      // render: (record: IJob) => {
      //   return record.client.address;
      // },
    },
    {
      id: 'type',
      label: t('table_head_title_jobType'),
      render: (record: IJob) => {
        return record?.type ? formatTitleCase(record?.type) : EMPTY_STRING;
      },
    },
    {
      id: 'tags',
      label: t('table_head_title_job_tags'),
      render: (record: IJob) => {
        if (record?.tags && record?.tags?.length > DEFAULT_NUMBER_ZERO) {
          const listTag = record?.tags?.map((item) => item?.name);

          return listTag?.join(', ');
        }

        return EMPTY_STRING;
      },
    },
    {
      id: 'source',
      label: t('table_head_title_lead_source'),
      render: (record: IJob) => {
        return record?.leadSource ? formatTitleCase(record?.leadSource.name) : EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: IJob) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            // onEdit={() => onEdit(record.id)}
            onChangeStatus={() => onChangeStatus(record)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const CustomerJobs = (props: Props) => {
  //#region Destructuring Props

  const { id: customerId } = useParams();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshJobByCustomerList = useSelector(selectIsRefreshJobByCustomerList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryGetListJob>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryGetListJob>({
    page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [jobListByCustomer, setJobListByCustomer] = useState<IJob[]>();
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalMissingPayment, setTotalMissingPayment] = useState<number>();
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [idsJob, setIdsJob] = useState<string[]>([]);

  const [jobSelectedIds, setJobSelectedIds] = useState<string[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [isShowChangeStatusJob, setIsShowChangeStatusJob] = useState<boolean>(false);
  const [jobSelected, setJobSelected] = useState<IJob | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const { paymentStatus, ...rest } = paramObject;
    const params = {
      ...rest,
      ...(paymentStatus !== JobFilterPaymentEnum.ALL && { paymentStatus }),
    };

    handleGetJobListByCustomer(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (!pagination) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  useEffect(() => {
    if (!isRefreshJobByCustomerList) return;

    const { paymentStatus, ...rest } = paramObject;
    const params = {
      ...rest,
      ...(paymentStatus !== JobFilterPaymentEnum.ALL && { paymentStatus }),
    };

    handleGetJobListByCustomer(params);
    dispatch(jobActions.setRefreshJobByCustomerList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshJobByCustomerList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetJobListByCustomer = (params: IQueryGetListJob) => {
    if (!customerId) return;
    setIsLoading(true);

    const body: IQueryParamById = {
      id: customerId,
      params: params,
    };

    dispatch(getJobListByCustomer(body))
      .unwrap()
      .then((resp) => {
        const { responses, pagination, totalJobMissingPayment } = resp.data;
        if (!responses) return;

        setJobListByCustomer(responses);
        setPagination(pagination);
        setTotalMissingPayment(totalJobMissingPayment);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNavigateToFormJob = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.createJob}?customerId=${customerId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.createJob}?customerId=${customerId}`);
        break;
    }
  };

  const handleApplyFilter = (values: IQueryGetListJob | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.paymentStatus) {
      setParamObject({ ...paramObject, ...values });
    }
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryGetListJob | undefined,
    onChange?: (value: IFilterValueChange<IQueryGetListJob>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryGetListJob = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleFilterChange = (
    name: keyof IQueryGetListJob,
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryGetListJob>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: name, value: value });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${id}`);
        break;
    }
  };

  const handleViewJob = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${id}`);
        break;
    }
  };

  const handleEditJob = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateJob(id)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateJob(id)}`);
        break;
    }
  };

  const handleNavigateJob = (jobId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${jobId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${jobId}`);
        break;
    }
  };

  const handleDeleteJob = (id: string) => {
    if (!id) return;

    setJobSelectedIds((prev) => [...prev, id]);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setJobSelectedIds([]);
  };

  const handleConfirmDeleteJob = () => {
    if (!jobSelectedIds || jobSelectedIds?.length === DEFAULT_NUMBER_ZERO) return;

    setIsLoading(true);

    dispatch(deleteJob({ deleteIds: jobSelectedIds }))
      .unwrap()
      .then((res) => {
        dispatch(jobActions.setRefreshJobByCustomerList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        setIsShowConfirmDelete(false);
      });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  // const handleRowsSelect = (ids: string[]) => {
  //   // TODO: Handle logic row select
  //   setIdsJob(ids);
  //   console.log(`[CustomerJobs] handleRowsSelect ids -> ${JSON.stringify(ids, null, 2)}`);
  // };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleChangeStatusJob = (job: IJob) => {
    if (!job) return;

    setJobSelected(job);
    setIsShowChangeStatusJob(true);
  };

  const handleCloseChangeStatusJob = () => {
    setIsShowChangeStatusJob(false);
    setJobSelected(null);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='customerJobsAdmin' className={cx('container')}>
      <BaseToolbar
        secondaryTitle={t('tile_quantity_job_missing_payment_page', {
          quantity: totalMissingPayment ?? DEFAULT_NUMBER_ZERO,
        })}
        textAction={t('button_add_job')}
        onAction={handleNavigateToFormJob}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryGetListJob>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.JOB_TYPE}
                      checked={value?.fields?.includes(SearchByFilterEnum.JOB_TYPE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_job_type')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.JOB_TAG}
                      checked={value?.fields?.includes(SearchByFilterEnum.JOB_TAG) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_job_tag')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.LEAD_SOURCE}
                      checked={value?.fields?.includes(SearchByFilterEnum.LEAD_SOURCE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_lead_source')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_job_payment')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'200px'}
                  options={jobFilterPaymentOptions}
                  value={value?.paymentStatus || JobFilterPaymentEnum.ALL}
                  onChange={(option) => handleFilterChange('paymentStatus', option, onChange)}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            t,
            handleNavigateJob,
            handleViewJob,
            handleEditJob,
            handleChangeStatusJob,
            handleDeleteJob
          )}
          dataSource={jobListByCustomer || []}
          // dataSelectedIds={idsJob}
          onPageChange={handlePageChange}
          // onSelectChange={handleRowsSelect}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDeleteJob}
        />
      )}

      {isShowChangeStatusJob && (
        <ChangeStatusJobModalComponent
          onClose={handleCloseChangeStatusJob}
          {...(jobSelected && { data: jobSelected })}
          type={PageTypeEnum.CUSTOMER}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default CustomerJobs;
